import React, { Component } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";
import {
  getAppVersionSettings,
  updateAppVersionSettings,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C,
  Panel,
  Field,
  Toggle,
  Btn,
  Chip,
  PillToggle,
  MiniStat,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * App version control — the two version numbers that decide whether the guest
 * app nags, blocks, or says nothing, restyled onto the console shell.
 *
 * The platform switch is in the topbar because it changes what every field
 * below means: android and ios are separate records, and switching re-reads
 * the one you are editing. Save sits beside it for the same reason — the form
 * is short, and the action should never be somewhere you have to hunt for.
 *
 * Re-skin only: getAppVersionSettings(platform) and updateAppVersionSettings()
 * are called with exactly the arguments the previous screen sent, and the
 * required-field check is the same manual toast it always was.
 */

const CSS = `
.avs .form-control{height:46px;padding:0 15px;background:#fff;border:1px solid #e6d8c4;
  border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.avs .form-control:focus{border-color:${C.sand};box-shadow:none}
.avs .form-control::placeholder{color:${C.taupeLight}}
.avs .form-control:disabled{background:#f7f1e7;color:${C.taupeLight}}
.avs textarea.form-control{height:auto;min-height:92px;padding:12px 15px;line-height:1.5;font-size:14px}
`;

const PLATFORMS = [
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
];

class AppVersionSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      isSaving: false,
      hasChanges: false,
      platform: "android",
      latestVersion: "",
      minVersion: "",
      updateUrl: "",
      updateMessage: "",
      forceUpdate: false,
      currentLiveVersion: "",
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = async () => {
    const { platform } = this.state;
    this.setState({ isLoading: true });

    const result = await getAppVersionSettings(platform);
    if (result && result.data) {
      const d = result.data;
      this.setState({
        latestVersion: d.latestVersion || "",
        minVersion: d.minVersion || "",
        updateUrl: d.updateUrl || "",
        updateMessage: d.updateMessage || "",
        forceUpdate: d.mustUpdate || false,
        currentLiveVersion: d.latestVersion || "",
        isLoading: false,
        hasChanges: false,
      });
    } else {
      this.setState({ isLoading: false, hasChanges: false });
    }
  };

  handleSave = async () => {
    const {
      platform,
      latestVersion,
      minVersion,
      updateUrl,
      updateMessage,
      forceUpdate,
    } = this.state;

    if (!latestVersion || !minVersion) {
      toast.error("Latest version and minimum version are required");
      return;
    }

    this.setState({ isSaving: true });

    const result = await updateAppVersionSettings({
      platform,
      latestVersion,
      minVersion,
      updateUrl,
      updateMessage,
      forceUpdate,
    });

    if (result && result.success) {
      toast.success("App version settings updated!");
      this.setState({
        currentLiveVersion: latestVersion,
        hasChanges: false,
        isSaving: false,
      });
    } else {
      toast.error(result?.message || "Failed to update settings");
      this.setState({ isSaving: false });
    }
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === "checkbox" ? checked : value,
      hasChanges: true,
    });
  };

  switchPlatform = (platform) => {
    if (platform === this.state.platform) return;
    this.setState({ platform }, this.loadSettings);
  };

  // --- presentation ---------------------------------------------------------

  /** A rule row: caption on the left, the control on the right. */
  ruleRow = (label, hint, control, last) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "14px 0",
        borderBottom: last ? "none" : `1px solid ${C.panel}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: C.ink }}>{label}</div>
        {hint && <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>{hint}</div>}
      </div>
      <div style={{ flex: "0 0 auto" }}>{control}</div>
    </div>
  );

  explain(tone, icon, title, body) {
    return (
      <div style={{ display: "flex", gap: 12 }}>
        <span
          className="ms"
          style={{
            fontSize: 19,
            color: tone,
            background: C.iconTint,
            padding: 7,
            borderRadius: 10,
            height: 34,
          }}
        >
          {icon}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, color: C.ink }}>{title}</div>
          <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight, lineHeight: 1.55 }}>
            {body}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      isLoading,
      isSaving,
      hasChanges,
      platform,
      latestVersion,
      minVersion,
      updateUrl,
      updateMessage,
      forceUpdate,
      currentLiveVersion,
    } = this.state;
    const write = canWrite();
    const platformLabel = platform === "ios" ? "iOS" : "Android";

    return (
      <ConsolePage
        title="App version control"
        subtitle={`What the ${platformLabel} guest app is told about updates`}
        actions={
          <React.Fragment>
            {hasChanges && (
              <Chip tone="warn">
                <span className="ms" style={{ fontSize: 16 }}>edit_note</span>
                Unsaved changes
              </Chip>
            )}
            <PillToggle
              options={PLATFORMS}
              value={platform}
              onChange={this.switchPlatform}
            />
            {write && (
              <Btn
                icon="save"
                onClick={this.handleSave}
                disabled={isLoading || isSaving || !hasChanges}
              >
                {isSaving ? "Saving…" : "Save settings"}
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        {isLoading ? (
          <Panel>
            <div style={{ padding: "40px 0", textAlign: "center", color: C.taupeLight }}>
              Loading settings…
            </div>
          </Panel>
        ) : (
          <div
            className="avs"
            style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20, alignItems: "start" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Panel
                title="Version gates"
                subtitle={`Compared against the ${platformLabel} build a guest is running`}
              >
                <div
                  style={{
                    marginTop: 18,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                  }}
                >
                  <Field
                    label="Latest version *"
                    hint='Below this, a guest sees "Update available" and can skip it'
                  >
                    <Input
                      type="text"
                      name="latestVersion"
                      value={latestVersion}
                      onChange={this.handleChange}
                      disabled={!write}
                      placeholder="e.g. 3.0.5"
                    />
                  </Field>
                  <Field
                    label="Minimum required version *"
                    hint="Below this, the prompt cannot be dismissed"
                  >
                    <Input
                      type="text"
                      name="minVersion"
                      value={minVersion}
                      onChange={this.handleChange}
                      disabled={!write}
                      placeholder="e.g. 3.0.0"
                    />
                  </Field>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Field
                    label="Update URL"
                    hint="Where the Update button sends them — Play Store or App Store"
                  >
                    <Input
                      type="text"
                      name="updateUrl"
                      value={updateUrl}
                      onChange={this.handleChange}
                      disabled={!write}
                      placeholder="Play Store or App Store URL"
                    />
                  </Field>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Field label="Update message" hint="Shown inside the prompt. Leave blank for the app's own wording.">
                    <Input
                      type="textarea"
                      name="updateMessage"
                      value={updateMessage}
                      onChange={this.handleChange}
                      disabled={!write}
                      rows={3}
                      placeholder="Message shown to users when an update is available"
                    />
                  </Field>
                </div>
              </Panel>

              <Panel title="Force update" subtitle="The blunt instrument — keep it for critical fixes">
                {this.ruleRow(
                  "Every guest must update",
                  forceUpdate
                    ? `On — no one can use the ${platformLabel} app until they have updated, whatever version they are on`
                    : "Off — only the version gates above decide who is prompted",
                  <Toggle
                    on={forceUpdate}
                    disabled={!write}
                    title="Force every guest to update"
                    onClick={() =>
                      this.setState({ forceUpdate: !forceUpdate, hasChanges: true })
                    }
                  />,
                  true
                )}
              </Panel>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <MiniStat
                dark
                label={`Live on ${platformLabel}`}
                value={currentLiveVersion || "Not set"}
                hint={
                  currentLiveVersion
                    ? "The version guests are being pointed at"
                    : "Nothing saved for this platform yet"
                }
              />

              <Panel title="How it works">
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
                  {this.explain(
                    "#2f7d33",
                    "check_circle",
                    "Latest version",
                    'Guests below it see a prompt with "Update" and "Later". They can carry on using the app.'
                  )}
                  {this.explain(
                    "#b26a00",
                    "warning",
                    "Minimum version",
                    "Guests below it see a prompt that cannot be dismissed. They must update to get any further."
                  )}
                  {this.explain(
                    C.red,
                    "release_alert",
                    "Force update",
                    "Ignores both numbers and blocks everyone. Use it for a critical security fix, then turn it off."
                  )}
                </div>
                <div style={{ marginTop: 18, fontSize: 12, color: C.taupeLight, lineHeight: 1.6 }}>
                  Android and iOS are stored separately — switching the platform
                  in the topbar loads that platform's own settings.
                </div>
              </Panel>
            </div>
          </div>
        )}
      </ConsolePage>
    );
  }
}

export default AppVersionSettings;
